// src/pages/AdminListDetail.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { PUBLIC_BASE } from '../utils/axiosInstance';

const toImageUrl = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && /^https?:\/\//i.test(raw)) return raw;
  const s = String(raw);
  return s.startsWith('/') ? `${PUBLIC_BASE}${s}` : `${PUBLIC_BASE}/${s}`;
};

const AdminListDetail = () => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList]   = useState(null);
  const [loading, setLd]  = useState(true);
  const [err, setErr]     = useState('');
  const [busy, setBusy]   = useState(false);

  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setErr('');
        setLd(true);
        // Keep your previous fetch style (GET /rankings then find)
        const res = await axios.get('/rankings');
        const found = (res.data || []).find(l => String(l._id) === String(listId));
        if (!found) throw new Error('List not found');
        if (alive) setList(found);
      } catch (e) {
        console.error('AdminListDetail failed:', e);
        if (alive) setErr('Failed to load list.');
      } finally {
        if (alive) setLd(false);
      }
    })();
    return () => { alive = false; };
  }, [listId]);

  const items = useMemo(() => (
    list ? (list.items || []).map((t, i) => ({ id: i, title: t })) : []
  ), [list]);

  const imgUrl = useMemo(() => {
    const raw = list?.image ?? list?.cover ?? list?.imageUrl ?? list?.imageURL ?? '';
    return toImageUrl(raw);
  }, [list]);

  const openPicker = () => fileRef.current?.click();

  const onFilePicked = async (file) => {
    if (!file) return;
    try {
      setBusy(true);
      setErr('');
      const fd = new FormData();
      fd.append('image', file);

      // Uses the new backend route you asked for
      await axios.patch(`/rankings/${listId}/image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // refresh list
      const res = await axios.get('/rankings');
      const found = (res.data || []).find(l => String(l._id) === String(listId));
      if (found) setList(found);
    } catch (e) {
      console.error('Upload image failed:', e);
      setErr(
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Failed to upload image.'
      );
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm('Delete this list? This cannot be undone.')) return;
    try {
      setBusy(true);
      setErr('');
      const candidates = [`/rankings/${listId}`, `/lists/${listId}`];
      let ok = false;
      for (const url of candidates) {
        try { await axios.delete(url); ok = true; break; } catch (_) {}
      }
      if (!ok) throw new Error('Delete endpoint not found');
      navigate('/admin/lists', { replace: true });
    } catch (e) {
      console.error('Delete failed:', e);
      setErr('Failed to delete list.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="scroll-shell">
      <div className="scroll-body">
        <button className="logout-btn" onClick={() => navigate(-1)} style={{ marginBottom:16 }}>
          ← Back
        </button>

        {loading && <div style={{ color:'#666' }}>Loading…</div>}
        {err && <div style={{ color:'#b91c1c', marginBottom: 12 }}>{err}</div>}

        {list && (
          <div className="rank-page">
            <h2 className="rank-title">{list.listName || list.name || 'Untitled'}</h2>

            {/* === New: square image + Change Image button (keeps your old page styling around it) === */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div
                style={{
                  width: 220,              // set the visual size of the square
                  aspectRatio: '1 / 1',    // keep it square and non-squished
                  borderRadius: 12,
                  overflow: 'hidden',
                  background: '#eef0f3',
                  border: '1px solid var(--border)',
                }}
              >
                {imgUrl ? (
                  <img
                    src={imgUrl}
                    alt="List cover"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#888',
                      fontSize: 14
                    }}
                  >
                    No image
                  </div>
                )}
              </div>

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => onFilePicked(e.target.files?.[0])}
              />
              <button
                className="logout-btn"
                onClick={openPicker}
                disabled={busy}
                style={{ background:'#fff', color:'#111', border:'1px solid var(--border)' }}
              >
                {busy ? 'Uploading…' : 'Change Image'}
              </button>
            </div>
            {/* === End new block === */}

            <ul className="rank-order">
              {items.map((it, idx) => (
                <li key={it.id}>
                  <span className="rank-chip">{idx + 1}</span>
                  <span className="rank-label">{it.title}</span>
                </li>
              ))}
            </ul>

            <div className="rank-actions" style={{ marginTop:18 }}>
              <button className="logout-btn" onClick={del} disabled={busy}>
                {busy ? 'Deleting…' : 'Delete list'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminListDetail;
